import { MoreOutlined } from '@ant-design/icons';
import { Button, Empty, Modal, Space, Spin, Tag, message } from 'antd';
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import ActionPopoverButton from '../../components/ActionPopoverButton';
import Table from '../../components/Table';
import { ColumnsType } from '../../types/ColumnTypes';
import { Permission } from '../../user/types/Permission';
import { useUser } from '../../user/userContext';
import { userHasPermission } from '../../user/userUtils';
import OpenClassAPI from '../OpenClassAPI';
import { OpenClass, OpenClassSession } from '../types/OpenClass';
import {
  sessionSpansDays,
  sortOpenClassesByStart,
  sortSessionsByStart,
  uniqueSessionInstructors,
} from '../utils/openClass';
import CoursePill from './CoursePill';
import OpenClassDateAndLocation from './OpenClassDateAndLocation';
import SessionCapacityList from './SessionCapacityList';
import OpenClassStatusBadge from './OpenClassStatusBadge';

type OpenClassCatalogProps = {
  loading: boolean;
  openClasses: OpenClass[];
  refreshOpenClasses: () => Promise<void>;
};

const openClassPath = (id: OpenClass['id']) => `/oppna-kurstillfallen/${id}`;

const OpenClassCatalog: React.FC<OpenClassCatalogProps> = ({
  loading,
  openClasses,
  refreshOpenClasses,
}) => {
  const [currentUser] = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const canDelete = userHasPermission(
    currentUser,
    Permission.COURSE_INSTANCE_DELETE,
  );

  const deleteOpenClass = useCallback(
    (openClass: OpenClass) => {
      Modal.confirm({
        content: t('views.OpenClasses.deleteOpenClassConfirm'),
        okButtonProps: { danger: true },
        title: t('common.delete'),
        onOk: async () => {
          try {
            await OpenClassAPI.deleteOpenClass(openClass.id);
            await refreshOpenClasses();
          } catch (error) {
            if (isAxiosError(error) && error.response?.status === 409) {
              message.error(
                t('views.OpenClasses.classDeleteRegistrationsError'),
              );
            } else {
              message.error(t('views.OpenClasses.deleteFailed'));
            }
          }
        },
      });
    },
    [refreshOpenClasses, t],
  );

  const columns = useMemo(
    (): ColumnsType<OpenClass> => [
      {
        title: t('common.status'),
        width: '12%',
        render: (openClass) => (
          <OpenClassStatusBadge
            endAt={openClass.endAt}
            status={openClass.status}
          />
        ),
      },
      {
        title: t('common.course'),
        width: '14%',
        render: (openClass) => <CoursePill name={openClass.course.name} />,
      },
      {
        contentClassName: 'block whitespace-nowrap pl-6 pr-1 py-3',
        title: t('views.OpenClasses.dateAndLocation'),
        width: '27%',
        render: (openClass) => (
          <OpenClassDateAndLocation openClass={openClass} />
        ),
      },
      {
        title: t('views.OpenClasses.participants'),
        width: '12%',
        render: (openClass) => (
          <SessionCapacityList sessions={openClass.sessions} />
        ),
      },
      {
        title: t('views.OpenClasses.sessions'),
        width: '10%',
        render: (openClass) =>
          t('views.OpenClasses.sessionCount', {
            count: openClass.sessions.length,
          }),
      },
      {
        contentClassName: 'block pl-3 pr-1 py-3',
        title: t('common.instructors'),
        render: (openClass) => (
          <Space size={[0, 4]} wrap>
            {uniqueSessionInstructors(openClass.sessions).map((instructor) => (
              <Tag key={instructor.id ?? instructor.email}>
                {instructor.name}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        cellClassName: 'align-middle',
        contentClassName: 'flex items-center justify-center py-3',
        title: '',
        notLinked: true,
        width: '48px',
        render: (openClass) => (
          <ActionPopoverButton
            absolutePlacement={false}
            icon={
              <Button
                aria-label={t('views.OpenClasses.actions')}
                icon={<MoreOutlined />}
                type="text"
              />
            }
            actionPopoverElements={[
              {
                rowElement: <div>{t('common.open')}</div>,
                action: () =>
                  navigate(openClassPath(openClass.id), {
                    state: { prevPage: location.pathname },
                  }),
              },
              {
                rowElement: (
                  <div className="text-warningRed">{t('common.delete')}</div>
                ),
                action: () => deleteOpenClass(openClass),
                hidden: !canDelete,
              },
            ]}
            placement="bottomRight"
            tooltipText={t('views.OpenClasses.actions')}
          />
        ),
      },
    ],
    [canDelete, deleteOpenClass, location.pathname, navigate, t],
  );

  const expandedRow = useCallback(
    (openClass: OpenClass) => {
      const sessionColumns: ColumnsType<OpenClassSession> = [
        {
          title: t('views.OpenClasses.session'),
          render: (_, index) => index + 1,
        },
        {
          title: t('common.status'),
          render: (session) => (
            <OpenClassStatusBadge
              endAt={session.endAt}
              status={session.status}
            />
          ),
        },
        {
          title: t('views.OpenClasses.time'),
          render: (session) =>
            `${dayjs(session.startAt).format('DD MMM YYYY HH:mm')} – ${dayjs(
              session.endAt,
            ).format(
              sessionSpansDays(session) ? 'DD MMM YYYY HH:mm' : 'HH:mm',
            )}`,
        },
        {
          title: t('common.location'),
          render: (session) => session.location || '–',
        },
        {
          title: t('views.OpenClasses.participants'),
          render: (session) =>
            `${session.assignedSeatCount ?? '–'} / ${session.capacity}`,
        },
        {
          title: t('common.instructors'),
          render: (session) => (
            <Space size={[0, 4]} wrap>
              {session.instructors.map((instructor) => (
                <Tag key={instructor.id ?? instructor.email}>
                  {instructor.name}
                </Tag>
              ))}
            </Space>
          ),
        },
      ];

      return (
        <Table
          cellClassName="align-top"
          columns={sessionColumns}
          rows={sortSessionsByStart(openClass.sessions)}
          rowKey={(session) => session.id}
        />
      );
    },
    [t],
  );

  const orderedOpenClasses = useMemo(
    () => sortOpenClassesByStart(openClasses),
    [openClasses],
  );

  return (
    <Space className="w-full" direction="vertical" size="large">
      <Table
        cellClassName="align-top"
        columns={columns}
        rows={orderedOpenClasses}
        expandable={{
          expandedRowRender: expandedRow,
          rowExpandable: (openClass) => openClass.sessions.length > 0,
        }}
        rowKey={(openClass) => openClass.id}
        rowHoverable={false}
        expandableCellClassName="align-middle"
      />
      {loading && <Spin size="large" className="mt-4 self-center" />}
      {!loading && openClasses.length === 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Space>
  );
};

export default OpenClassCatalog;
